import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Send, Bot, User as UserIcon, ShieldAlert, Camera, BarChart3, MessageSquareText } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Card, PageHeader, Button } from "@/components/ui-kit";

/**
 * Iter v31 — Karyawan CS chat page (/karyawan/chat-hr).
 * Karyawan can ask absensi/gaji questions. AI answers in-scope; off-topic auto-escalates to admin HR.
 */

const SUGGESTIONS = [
  "Berapa total jam kerja saya bulan ini?",
  "Kapan jadwal pencairan gaji?",
  "Saya lupa clock-out kemarin, bagaimana?",
  "Bagaimana cara mengajukan cuti?",
  "Berapa kali saya terlambat bulan ini?",
];

function fmtTime(ts) {
  if (!ts) return "";
  try { return new Date(ts).toLocaleTimeString("id-ID", { hour: "2-digit", minute: "2-digit" }); } catch { return ""; }
}

export default function KaryawanChatHR() {
  const { user } = useAuth();
  const [thread, setThread] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(true);
  const bottomRef = useRef(null);
  // Iter v34r (#1) — same scroll fix as Inbox HR: only scroll on count grow + container-bound
  const prevMsgCount = useRef(0);
  const messagesContainerRef = useRef(null);

  const loadMessages = async (threadId) => {
    if (!threadId) return;
    try {
      const r = await api.get(`/chat/threads/${threadId}/messages`);
      setMessages(r.data || []);
    } catch { /* */ }
  };

  const ensureThread = async () => {
    try {
      const r = await api.post("/chat/karyawan-cs/ensure-thread");
      setThread(r.data);
      await loadMessages(r.data.id);
    } catch (e) {
      console.error(e);
    } finally { setLoading(false); }
  };

  useEffect(() => { ensureThread(); }, []);

  // Iter v34x (#5) — Karyawan request close chat.
  const requestClose = async () => {
    if (!thread) return;
    if (!window.confirm("Tutup chat ini? Chat akan hilang dari daftar Anda. Anda bisa buka kembali kapan saja untuk memulai chat baru.")) return;
    try {
      await api.post(`/chat/karyawan-cs/threads/${thread.id}/close`, { reason: "Karyawan request via UI button" });
      // Setelah close, langsung ensure-thread agar UI bersih (akan create thread BARU).
      setMessages([]);
      setThread(null);
      await ensureThread();
    } catch (e) {
      // Fallback: kalau gagal, beri pesan
      console.error(e);
    }
  };

  // Poll for new messages every 5s (AI replies arrive after 60s for in-scope)
  useEffect(() => {
    if (!thread) return;
    const t = setInterval(() => loadMessages(thread.id), 5000);
    return () => clearInterval(t);
  }, [thread]);

  useEffect(() => {
    if (messages.length > prevMsgCount.current) {
      const el = messagesContainerRef.current;
      if (el) el.scrollTop = el.scrollHeight;
    }
    prevMsgCount.current = messages.length;
  }, [messages]);

  const send = async (text) => {
    const body = (text ?? input).trim();
    if (!body || !thread || sending) return;
    setSending(true);
    try {
      await api.post(`/chat/threads/${thread.id}/messages`, { body });
      setInput("");
      await loadMessages(thread.id);    } finally { setSending(false); }
  };

  if (loading) return <div className="p-6 text-center text-sm text-stone-400">Memuat percakapan…</div>;

  return (
    <div className="min-h-full bg-[#f6f5f0]">
    <div className="max-w-3xl mx-auto p-4 md:p-6 lg:p-8 space-y-4" data-testid="karyawan-chat-hr-page">
      <PageHeader overline="Karyawan" title="Chat HR / Admin"
        description="Ajukan pertanyaan seputar absensi dan pembayaran gaji. AI akan membantu, admin HR juga dapat bergabung." />
      <div className="flex flex-wrap gap-2">
        <Link to="/karyawan/absen" className="inline-flex items-center gap-1.5 px-4 py-2 text-xs rounded-full bg-white border border-stone-300 text-stone-700 hover:border-stone-400 hover:bg-stone-50"><Camera className="h-3.5 w-3.5" /> Absen Hari Ini</Link>
        <Link to="/karyawan/ringkasan" className="inline-flex items-center gap-1.5 px-4 py-2 text-xs rounded-full bg-white border border-stone-300 text-stone-700 hover:border-stone-400 hover:bg-stone-50"><BarChart3 className="h-3.5 w-3.5" /> Ringkasan Saya</Link>
        <Link to="/karyawan/chat-hr" className="inline-flex items-center gap-1.5 px-4 py-2 text-xs rounded-full bg-stone-900 text-white"><MessageSquareText className="h-3.5 w-3.5" /> Chat HR</Link>
      </div>

      <Card className="p-3 bg-blue-50 border-blue-200 text-xs text-blue-900 flex items-start gap-2">
        <ShieldAlert className="h-4 w-4 text-blue-600 mt-0.5" />
        <div>
          Chat ini hanya untuk pertanyaan <b>absensi</b> dan <b>pembayaran gaji</b>. Pertanyaan di luar topik akan diteruskan langsung ke admin HR.
        </div>
      </Card>

      {/* Messages */}
      <Card className="p-0 flex flex-col" style={{ minHeight: "60vh" }} data-testid="karyawan-chat-thread">
        <div ref={messagesContainerRef} className="flex-1 overflow-y-auto p-4 space-y-3" style={{ maxHeight: "60vh" }}>
          {messages.length === 0 && (
            <div className="text-center py-12 space-y-3" data-testid="karyawan-chat-empty">
              <Bot className="h-10 w-10 text-terra-400 mx-auto" />
              <p className="text-sm text-stone-500">Belum ada pesan. Tanyakan sesuatu tentang absensi atau gaji.</p>
              <div className="flex flex-wrap gap-2 justify-center pt-2">
                {SUGGESTIONS.map((s, i) => (
                  <button key={i} onClick={() => send(s)} className="text-xs px-3 py-1.5 bg-stone-100 hover:bg-stone-200 rounded-full" data-testid={`karyawan-chat-suggestion-${i}`}>
                    {s}
                  </button>
                ))}
              </div>
            </div>
          )}
          {messages.map((m) => {
            const isMe = m.sender_id === user?.id;
            const isBot = m.is_bot || m.sender_id === "ai_assistant";
            return (
              <div key={m.id} className={`flex gap-2 ${isMe ? "justify-end" : "justify-start"}`} data-testid={`karyawan-chat-msg-${m.id}`}>
                {!isMe && (
                  <div className={`w-7 h-7 flex-shrink-0 rounded-full flex items-center justify-center ${isBot ? "bg-terra-100 text-terra-700" : "bg-stone-200 text-stone-700"}`}>
                    {isBot ? <Bot className="h-3.5 w-3.5" /> : <UserIcon className="h-3.5 w-3.5" />}
                  </div>
                )}
                <div className={`max-w-[75%] ${isMe ? "items-end" : "items-start"} flex flex-col`}>
                  {!isMe && (
                    <span className="text-[10px] text-stone-500 px-1 mb-0.5">
                      {isBot ? "Asisten HR (AI)" : (m.sender_name || "Admin")}
                    </span>
                  )}
                  <div className={`px-3 py-2 rounded-2xl text-sm ${isMe ? "bg-blue-600 text-white" : (isBot ? "bg-blue-50 border border-blue-100 text-stone-800" : "bg-stone-100 text-stone-900")}`}>
                    <div className="whitespace-pre-wrap break-words">{m.body}</div>
                  </div>
                  <span className="text-[10px] text-stone-400 px-1 mt-0.5">{fmtTime(m.ts)}</span>
                </div>
              </div>
            );
          })}
          <div ref={bottomRef} />
        </div>
        {/* Composer */}
        <div className="border-t border-stone-200 p-3 flex gap-2">
          <input type="text" value={input} onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
            placeholder="Tulis pertanyaan tentang absensi atau gaji..."
            className="flex-1 border border-stone-300 rounded-lg px-3 py-2 text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none"
            disabled={sending}
            data-testid="karyawan-chat-input" />
          <Button onClick={() => send()} disabled={sending || !input.trim()} data-testid="karyawan-chat-send">
            <Send className="h-3.5 w-3.5" /> Kirim
          </Button>
          {thread && (
            <Button variant="ghost" onClick={requestClose} className="text-red-700 hover:bg-red-50 !border-red-200" title="Tutup chat" data-testid="karyawan-chat-close">
              Tutup Chat
            </Button>
          )}
        </div>
      </Card>

      <p className="text-[11px] text-stone-500 text-center">
        AI akan menjawab dalam 1 menit kecuali admin HR membalas duluan. Untuk pertanyaan urgent, ketik &quot;hubungkan ke admin&quot;.
      </p>
    </div>
    </div>
  );
}
